import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  type NineSegmentDisplayColumnKey,
  nineSegmentIndicators,
  type NineSegmentSortColumnKey,
} from '~/apps/ninesegsAnalyzer/utils/customerAnalysis'
import {
  AnalysisCommentBlock,
  useGenerateSingleComment,
} from '~/components/features/AnalysisCommentBlock'
import { DisplayToggleButton } from '~/components/features/DisplayToggleButton'
import { ShareDropDownMenuButton } from '~/components/features/ShareDropDownMenuButton'
import {
  SortButton,
  type SortButtonProps,
} from '~/components/features/SortButton'
import { Table } from '~/components/features/Table'
import { TableContainer } from '~/components/features/TableContainer'
import { AmplitudeProvider } from '~/components/providers/AmplitudeEventProvider'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useSurveyQueryActiveBrandGroup } from '~/hooks/useSurveyQuery'
import {
  type DisplayColumnItem,
  exists,
  formatNineSegment,
  nineSegmentIndicatorLabelMap,
  reversedNineSegments,
  serializeTableToText,
} from '~/utils'

import {
  hiddenRowIdsAtom,
  nineSegmentCommentAtom,
  nineSegmentHiddenColumnKeysAtom,
  nineSegmentSortValueAtom,
  savedNineSegmentCommentAtom,
} from '../atoms'
import { useRestoringParams } from '../hooks/restoringParams/useRestoringParams'
import { useDisplayRowItem } from '../hooks/useDisplayRowItem'
import { useFigureInfo } from '../hooks/useFigureInfo'
import { useNineSegmentsTableColumns } from '../hooks/useNineSegmentsTableColumns'
import { useNineSegmentsTableInfo } from '../hooks/useNineSegmentsTableInfo'
import { useSurveyId } from '../hooks/useSurveyId'

import styles from './NineSegmentsTable.css'

// useLogEvent()はAmplitudeProviderの子孫でないと withSuffix が効かないため、
// Providerをレンダーする側とuseLogEvent()を呼ぶ側を分離する
export const NineSegmentsTable = () => (
  <AmplitudeProvider analysisName="distribution" eventNameSuffix="_9segs">
    <NineSegmentsTableInner />
  </AmplitudeProvider>
)

const NineSegmentsTableInner = () => {
  const [comment, setComment] = useAtom(nineSegmentCommentAtom)
  const savedComment = useAtomValue(savedNineSegmentCommentAtom)
  const setSavedComment = useSetAtom(savedNineSegmentCommentAtom)
  const { generate, isGenerating } = useGenerateSingleComment()
  const surveyId = useSurveyId()
  const brandGroup = useSurveyQueryActiveBrandGroup()
  const columns = useNineSegmentsTableColumns()
  const tableInfo = useNineSegmentsTableInfo({
    indicatorPad: styles.cellPadColumn,
  })
  const { t } = useTranslation()

  const figureInfo = useFigureInfo('9segs')
  const restoringParams = useRestoringParams('9segs')
  const image = <Table columns={columns} tableInfo={tableInfo} />

  const [sortValue, setSortValue] = useAtom(nineSegmentSortValueAtom)

  const displayRowItems = useDisplayRowItem('9segs')
  const setHiddenRowIds = useSetAtom(hiddenRowIdsAtom)

  const [hiddenColumnKeys, setHiddenColumnKeys] = useAtom(
    nineSegmentHiddenColumnKeysAtom,
  )
  const displayColumnItems = React.useMemo<
    DisplayColumnItem<NineSegmentDisplayColumnKey>[]
  >(
    () => [
      ...reversedNineSegments.map((nineSegment) => ({
        columnKey: nineSegment,
        isVisible: !hiddenColumnKeys.includes(nineSegment),
        label: formatNineSegment(nineSegment, t('9segs_distribution_t'), t),
      })),
      ...nineSegmentIndicators.map((indicator) => ({
        columnKey: indicator,
        isVisible: !hiddenColumnKeys.includes(indicator),
        label: t('core_metrix_ninesegmentcore_metrixlabelmapcore_metrix', {
          nineSegmentIndicatorLabel: nineSegmentIndicatorLabelMap[indicator],
        }),
      })),
    ],
    [hiddenColumnKeys, t],
  )

  const sortButtonItems: SortButtonProps<NineSegmentSortColumnKey>['items'] =
    React.useMemo(
      () => [
        ...reversedNineSegments.map((nineSegment) => ({
          columnKey: nineSegment,
          label: formatNineSegment(nineSegment, t('9segs_distribution_t'), t),
        })),
        ...nineSegmentIndicators.map((indicator) => ({
          columnKey: indicator,
          label: t('core_metrix_ninesegmentcore_metrixlabelmapcore_metrix_1', {
            nineSegmentIndicatorLabel: nineSegmentIndicatorLabelMap[indicator],
          }),
        })),
      ],
      [t],
    )

  const logEvent = useLogEvent()
  const handleAutoGenerate = React.useCallback(async () => {
    const result = await generate({
      brandName: brandGroup?.name ?? '',
      // TODO: filterDescription は暫定で空文字（対象条件ブロックは出力されない）。実フィルター条件を渡す
      filterDescription: '',
      filterPatternIndex: 0,
      outputType: 'customerAnalysis',
      segmentMode: 'nineSegs',
      surveyId,
      tabulationData: serializeTableToText(columns, tableInfo),
    })
    if (exists(result)) {
      setComment(result)
      logEvent('Generate AI comment', { withSuffix: true })
    }
  }, [
    generate,
    logEvent,
    brandGroup?.name,
    surveyId,
    columns,
    tableInfo,
    setComment,
  ])

  return (
    <>
      <AnalysisCommentBlock
        isGenerating={isGenerating}
        savedComment={savedComment}
        saveDocumentImage={{
          commentReadOnly: true,
          figureInfo,
          image,
          initialComment: comment,
          onSaved: (saved) => setSavedComment(saved),
          restoringParams,
        }}
        value={comment}
        onAutoGenerate={handleAutoGenerate}
        onCommentChange={setComment}
      />
      <TableContainer
        header={
          <>
            <SortButton
              items={sortButtonItems}
              value={sortValue}
              onChange={setSortValue}
            />

            <DisplayToggleButton
              columnItems={displayColumnItems}
              rowItems={displayRowItems}
              onHiddenColumnChange={setHiddenColumnKeys}
              onHiddenRowChange={setHiddenRowIds}
            />

            <ShareDropDownMenuButton
              copyToClipboard={{
                columns,
                figureInfo,
                restoringParams,
                tableInfo,
              }}
              downloadAsPng={{ image }}
              getSharedUrl={{ restoringParams }}
              saveDocumentImage={{
                commentReadOnly: true,
                figureInfo,
                image,
                initialComment: comment,
                onSaved: (saved) => setSavedComment(saved),
                restoringParams,
              }}
            />
          </>
        }
        id="9segs"
        title="9segs"
      >
        <Table columns={columns} stickyColumns={2} tableInfo={tableInfo} />
      </TableContainer>
    </>
  )
}
